// defaultDisplayName is unbounded server-side, but the panel only has room for ~30
// characters. Longer names are cut with an ellipsis; the shortName handle stays intact.
export const DISPLAY_NAME_MAX_CHARS = 28;

/**
 * Truncate a long agent display name for the 318px panel.
 *
 * @param {string} name - The agent's display name
 * @param {number} [maxChars] - Character budget before truncating
 * @returns {string} The name, cut with "..." when it exceeds the budget
 */
export const truncateDisplayName = (name, maxChars = DISPLAY_NAME_MAX_CHARS) => {
  const trimmed = (name || "").trim();
  return trimmed.length > maxChars ? `${trimmed.slice(0, maxChars)}...` : trimmed;
};

// "Ada" | "Ada and Bo" | "Ada, Bo, and Cy"
const formatAgentNameList = (names) => {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
};

/**
 * Scheduled-agent Case-2 (partial failure) toast copy: names the agents that failed to
 * auto-activate when their (already-truncated) display names are known, otherwise falls back
 * to a name-free "some" message. The all-failed / fetch-failed copy is
 * SCHEDULED_AGENTS_ALL_INACTIVE_MESSAGE in constants.jsx.
 *
 * @param {string[]} failedNames - resolved, truncated display names of the failed agents
 * @returns {string}
 */
export const scheduledAgentsPartialErrorMessage = (failedNames) =>
  failedNames.length > 0
    ? `Some agents are inactive — ${formatAgentNameList(failedNames)}. Toggle them on in Settings.`
    : "Some agents are inactive. Toggle them on in Settings.";

/**
 * The two-character avatar initials for an agent, taken from its shortName when it has
 * one and falling back to the display name — shortName is optional server-side.
 *
 * @param {{shortName?: string, name?: string}} agent
 * @returns {string} first + last word initials, e.g. "AA" for "Amazon Meetings Assistant";
 *                   the first two letters when it is a single word, e.g. "OP" for "ops"
 */
export const getAgentInitials = (agent) => {
  const source = (agent?.shortName || "").trim() || (agent?.name || "").trim();
  const words = source.split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
};

/**
 * The @-mention handle for an agent, built from its globally unique shortName.
 *
 * @param {{shortName?: string}} agent
 * @returns {string} e.g. "@ops-agent"
 */
export const getAgentHandle = (agent) => {
  const shortName = (agent?.shortName || "").trim();
  return shortName ? `@${shortName}` : "";
};
